import {
  isCorrectLength,
  numericValidations,
  isWithinPrecision,
  isNotBlank,
  isNotTouched,
  reduceValidationResults,
} from '../validators';
import flatMapDeep from 'lodash/flatMapDeep';
import { t } from '../../../utils/translate';
import datetimeFormats from '../inputs/formats';
import moment from 'moment';

export const validateOpenRequired = (number_of_fields) => state => {
    const fieldValids =  state.values.reduce(
      (indeces, field) =>
        indeces.push((isNotBlank(field) || isNotTouched(field))) && indeces, []
    );

    const isValid = fieldValids.every(fieldIsValid => fieldIsValid);
    let errorMessages;

    if (isValid) {
      errorMessages = null
    } else if (number_of_fields === 1) {
      errorMessages = [`This field is required`]
    } else {
      errorMessages = [t('must_fill_all')]
    }

  return reduceValidationResults(state, {
    errorMessages,
    fieldValids,
    isValid,
  })
};

export const validateOpenNumeric = (validations) => state => {
  let errorMessages;
  const fieldType = state.fieldType
  const fieldValids = state.values.reduce(
    (indeces, field) =>
      indeces.push((numericValidations(validations, field, fieldType)) && (isWithinPrecision(validations, field, fieldType))) && indeces, []
  );

  const isValid = fieldValids.every(fieldIsValid => fieldIsValid);

  if (!isValid) {
    if (validations.max && !validations.min && validations.precision) {
      errorMessages = [`Number must have ${validations.precision} or less decimal places and be no greater than ${validations.max}`]
    } else if (validations.min && !validations.max && validations.precision) {
      errorMessages = [`Number must have ${validations.precision} decimal or less places and be no less than ${validations.min}`]
    } else if (validations.max && !validations.min && !validations.precision) {
      errorMessages = [`Number must be no greater than ${validations.max}`]
    } else if (validations.min && !validations.max && !validations.precision) {
      errorMessages = [`Number must be no less than ${validations.min}`]
    } else if (validations.max && validations.min && validations.precision) {
      errorMessages = [`Number must have ${validations.precision} or less decimal places and be between ${validations.min} and ${validations.max}`]
    } else if (validations.max && validations.min && !validations.precision) {
      errorMessages = [`Number must be between ${validations.min} and ${validations.max}`]
    } else if (validations.precision) {
      errorMessages = [`Number must have ${validations.precision} or less decimal places`]
    }
  }

  return reduceValidationResults(state, {
    errorMessages,
    fieldValids,
    isValid,
  })
}

export const validateOpenZipcode = (validations) => state => {
  let errorMessages;

  const fieldType = state.fieldType
  const fieldValids = state.values.reduce(
    (indeces, field) =>
      indeces.push((isCorrectLength(validations, field, fieldType))) && indeces, []
  );

  const isValid = fieldValids.every(fieldIsValid => fieldIsValid);

  if (!isValid) {
    errorMessages = [`Zip code must be ${validations.zipcode_length} digits`]
  }

  return reduceValidationResults(state, {
    errorMessages,
    fieldValids,
    isValid,
  })
}

export const matrixOpenValidator = state => {
  const flatValues = flatMapDeep(state.rows, row => row.values);
  const fieldValids = flatValues.map(
    field => isNotBlank(field) || isNotTouched(field)
  );
  const hasValues = fieldValids.every(v => v === true);
  const isForgiven = flatValues.some(v => !v.isTouched);
  const isValid = hasValues || isForgiven;
  const errorMessages = isValid ? null : [t('must_fill_all')];
  return reduceValidationResults(state, {
    errorMessages,
    fieldValids,
    isValid,
  });
};

const isDateTimeValid = (field_type, value) => {
  const date = moment(value, datetimeFormats[field_type]);
  return date.isValid();
};

export const validateDatetime = field_type => state => {
  const fieldValids = state.values.map(field => {
    const isBlank = !field.value.toString().trim();
    const isForgiven = isNotTouched(field) || isBlank;
    return isForgiven || isDateTimeValid(field_type, field.value);
  });
  const isValid = fieldValids.every(fieldIsValid => fieldIsValid);
  const errorMessages = isValid ? null : [t(`invalid_${field_type}`)];
  return reduceValidationResults(state, {
    errorMessages,
    fieldValids,
    isValid,
  });
};

export const matrixDatetimeValidator = field_type => state => {
  const flatValues = flatMapDeep(state.rows, row => row.values);
  const fieldValids = flatValues.map(
    field => isDateTimeValid(field_type, field.value) || isNotTouched(field)
  );
  const isValid = fieldValids.every(v => v === true);
  const errorMessages = isValid ? null : [t(`invalid_${field_type}`)];
  return reduceValidationResults(state, {
    errorMessages,
    fieldValids,
    isValid,
  });
};

export const matrixNumericValidator = (validations, fieldType) => state => {
  let errorMessages;
  const flatValues = flatMapDeep(state.rows, row => row.values);
  const fieldValids = flatValues.map(
    field => (numericValidations(validations, field, fieldType) && (isWithinPrecision(validations, field, fieldType)))
  );

  const hasValues = fieldValids.every(validatedFieldValue => validatedFieldValue === true);
  const isForgiven = flatValues.some(v => !v.isTouched);
  const isValid = hasValues || isForgiven;

  if (!isValid) {
    if (validations.max && !validations.min && validations.precision) {
      errorMessages = [`Number must have ${validations.precision} or less decimal places and be no greater than ${validations.max}`]
    } else if (validations.min && !validations.max && validations.precision) {
      errorMessages = [`Number must have ${validations.precision} decimal or less places and be no less than ${validations.min}`]
    } else if (validations.max && !validations.min && !validations.precision) {
      errorMessages = [`Number must be no greater than ${validations.max}`]
    } else if (validations.min && !validations.max && !validations.precision) {
      errorMessages = [`Number must be no less than ${validations.min}`]
    } else if (validations.max && validations.min && validations.precision) {
      errorMessages = [`Number must have ${validations.precision} or less decimal places and be between ${validations.min} and ${validations.max}`]
    } else if (validations.max && validations.min && !validations.precision) {
      errorMessages = [`Number must be between ${validations.min} and ${validations.max}`]
    } else if (validations.precision) {
      errorMessages = [`Number must have ${validations.precision} or less decimal places`]
    }
  }

  return reduceValidationResults(state, {
    errorMessages,
    fieldValids,
    isValid,
  });
};

export const matrixZipcodeValidator = (validations, fieldType) => state => {
  let errorMessages;
  const flatValues = flatMapDeep(state.rows, row => row.values);
  const fieldValids = flatValues.map(
    field => (isCorrectLength(validations, field, fieldType))
  );

  const hasValues = fieldValids.every(validatedFieldValue => validatedFieldValue === true);
  const isForgiven = flatValues.some(v => !v.isTouched);
  const isValid = hasValues || isForgiven;

  if (!isValid) {
    errorMessages = [`Zip code must be ${validations.zipcode_length} digits`]
  }

  return reduceValidationResults(state, {
    errorMessages,
    fieldValids,
    isValid,
  });
}
